"use client"

import { Fragment, useState } from "react"
import clsx from "clsx"
import dayjs from "dayjs"
import { useForm } from "react-hook-form"

import type { UnlikelyCartLineInput } from "@unlikelystudio/commerce-connector"
import { Field } from "@unlikelystudio/react-ecommerce-hooks"

import { formatCheckoutItems } from "~/lib/shopify/utils/format-checkout-items"
import { useAddToCart } from "~/hooks/useAddToCart"
import { AddToCartCta } from "~/components/ui/AddToCartCta"
import type { ProductHeaderProps } from "~/components/ui/ProductHeader"
import type { serializeProductHeaderSP } from "~/components/ui/ProductHeader/_data/serializer"
import { EMAIL_REGEX } from "~/components/abstracts/Input/utils/regex"
import LayoutForm from "~/components/abstracts/LayoutForm"
import Select from "~/components/abstracts/Select"
import { FORM_NAMES, FormTemplate } from "~/components/forms/FormTemplate"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"

import { sprinkles } from "~/styles/sprinkles.css"

import * as css from "./styles.css"

const GIFT_CARD_ATTRIBUTES = {
  RECIPIENT_EMAIL: "Recipient email",
  RECIPIENT_NAME: "Recipient name",
  MESSAGE: "Message",
  DATE: "Send on",
} as const

type GiftCardProps = Pick<ProductHeaderProps, "trackingData"> & {
  serializedProduct: ReturnType<typeof serializeProductHeaderSP>
}

type GiftCardForm = {
  amount: string
  name: string
  email: string
  message: string
  date: string
  sendType: string
}

export default function GiftCard({ serializedProduct, trackingData }: GiftCardProps) {
  const t = useTranslate()
  const form = useForm<GiftCardForm>()

  const [sendToRecipient, setSendToRecipient] = useState(true)

  const selectOptions = serializedProduct.variants.map((variant) => ({
    value: variant.id ?? "",
    label: variant.formattedPrice ?? "",
  }))

  // The classic onChange won't work correctly on the form
  const [activeVariant, setActiveVariant] = useState(
    serializedProduct.variants.find((variant) => variant.id === form.getValues("amount")) ??
      serializedProduct.variants?.[0]
  )

  const onChangeAmount = () => {
    if (form.getValues("amount") === activeVariant?.id) {
      return
    }

    setActiveVariant(
      serializedProduct.variants.find((variant) => variant.id === form.getValues("amount")) ??
        serializedProduct.variants?.[0]
    )
  }

  const resetForm = () => {
    form.reset()
    setActiveVariant(serializedProduct.variants?.[0])
  }

  const { mutate: addCartLines, isLoading } = useAddToCart(activeVariant, () => {
    resetForm()
  })

  const onSubmit = form.handleSubmit(({ email, name, message, date }) => {
    if (!activeVariant) return

    const attributes: UnlikelyCartLineInput["attributes"] = [
      email && sendToRecipient && { key: GIFT_CARD_ATTRIBUTES.RECIPIENT_EMAIL, value: email?.toString() },
      name && sendToRecipient && { key: GIFT_CARD_ATTRIBUTES.RECIPIENT_NAME, value: name?.toString() },
      message && sendToRecipient && { key: GIFT_CARD_ATTRIBUTES.MESSAGE, value: message?.toString() },
      date && sendToRecipient && { key: GIFT_CARD_ATTRIBUTES.DATE, value: date?.toString() },
    ]?.filter(Boolean)

    const checkoutItems = formatCheckoutItems(
      {
        variant: activeVariant,
        trackingData,
      },
      sendToRecipient ? [...attributes, { key: "__shopify_send_gift_card_to_recipient", value: "true" }] : []
    )

    addCartLines(checkoutItems)
  })

  const minDate = new Date().toISOString().split("T")[0]
  // shopify API has a limit of 90 days of scheduled gift card recipient notification
  const maxDate = dayjs(minDate).add(90, "days").toISOString().split("T")[0]

  return (
    <FormTemplate formName={FORM_NAMES.GIFT_CARD} className={css.GiftCard}>
      <LayoutForm className={clsx(sprinkles({ marginBottom: 17 }))} form={form}>
        {serializedProduct.variants && serializedProduct.variants.length > 0 && (
          <Fragment>
            <Select
              label={t("gift_card_amount_label")}
              id="amount"
              options={selectOptions}
              {...form.register("amount", {
                onChange: onChangeAmount,
              })}
            />

            <Field
              name="sendToRecipient"
              type="checkbox"
              label={t("gift_card_send_to_recipient_label")}
              inputProps={{
                className: clsx(sprinkles({ cursor: "pointer" })),
                defaultChecked: sendToRecipient,
              }}
              labelProps={{
                className: clsx(css.sendTypeLabel),
              }}
              registerOptions={{
                onChange: (e) => {
                  setSendToRecipient(e.target.checked)
                },
              }}
            />

            {sendToRecipient && (
              <Fragment>
                <Field
                  label={t("gift_card_recipient_name_label")}
                  labelRequiredText="*"
                  labelProps={{ className: clsx(css.labelBackground, form.formState.errors.name && css.labelError) }}
                  registerOptions={{ required: t("error_required") }}
                  name="name"
                  type="text"
                />

                <Field
                  label={t("gift_card_recipient_email_label")}
                  labelProps={{ className: clsx(css.labelBackground, form.formState.errors.email && css.labelError) }}
                  name="email"
                  type="email"
                  labelRequiredText="*"
                  registerOptions={{
                    required: t("error_required"),
                    pattern: {
                      value: EMAIL_REGEX,
                      message: t("error_email"),
                    },
                  }}
                />

                <Field
                  label={t("gift_card_message_label")}
                  name="message"
                  labelProps={{
                    className: clsx(css.labelBackground, form.formState.errors.message && css.labelError),
                  }}
                  type="text"
                  isTextarea
                  registerOptions={{
                    required: t("error_required"),
                    // shopify API has a limit of 200 characters for the gift card message
                    maxLength: { value: 200, message: t("error_max_length") },
                  }}
                />

                <Field
                  labelRequiredText="*"
                  label={t("gift_card_date_label")}
                  labelProps={{ className: clsx(css.labelBackground, form.formState.errors.date && css.labelError) }}
                  name="date"
                  type="date"
                  inputProps={{
                    defaultValue: minDate,
                  }}
                  registerOptions={{
                    min: minDate,
                    max: maxDate,
                    required: t("error_required"),
                  }}
                />
              </Fragment>
            )}
          </Fragment>
        )}
      </LayoutForm>
      <AddToCartCta
        withPrice
        customIsLoading={isLoading}
        onClick={onSubmit}
        activeVariants={{
          id: activeVariant?.id,
          price: activeVariant?.price,
          sku: activeVariant?.sku,
          vpPrice: activeVariant?.vpPrice,
        }}
      />
    </FormTemplate>
  )
}
